import { Fragment, useEffect, useRef, useState } from 'react'
import { EChartsType, format, init, use } from 'echarts/core'
import { GridComponent, TooltipComponent } from 'echarts/components'
import { BarChart } from 'echarts/charts'
import { SVGRenderer } from 'echarts/renderers'
import { useOutsideClick } from '../../hooks'
import {
  captureScreenshot,
  formatNum,
  manageCatTotals,
  parseDate,
  toggleExpandContextMenu
} from '../../utils/functions'
import { ContextSchema, FormSchema, QuestionSchema } from '../../utils/types/form'
import { SubmissionSchema } from '../../utils/types/submission'
import { barChartDemo } from '../../data/demo'

const isDemo = process.env.REACT_APP_DEMO === 'true'

export const BarGraph: React.FC<{
  form: FormSchema | null
  submissions: SubmissionSchema[]
  questions: {
    allQuestions: QuestionSchema[]
    infoQuestions: QuestionSchema[]
  }
  plotQuestions: {
    plotNumQuestions: QuestionSchema[]
    plotCatQuestions: QuestionSchema[]
  }
  filters: {
    filterOptions: Map<string, Set<string>>
    isFilterOptionsOpen: boolean
    setIsFilterOptionsOpen: React.Dispatch<React.SetStateAction<boolean>>
    filterOptionsTriggerRef: React.RefObject<null>
    filterOptionsContextRef: React.RefObject<null>
    handleFilterOptionsChange: Function
    getIsFilterOptionsAllSelected: Function
    isFilterOptionsAllSelected: boolean
  }
  theme: { name: string; hex: string }
}> = (props) => {
  const { form, submissions, questions, plotQuestions, filters, theme } = props
  const { allQuestions, infoQuestions } = questions
  const { plotNumQuestions, plotCatQuestions } = plotQuestions
  const {
    filterOptions,
    isFilterOptionsOpen,
    setIsFilterOptionsOpen,
    filterOptionsTriggerRef,
    filterOptionsContextRef,
    handleFilterOptionsChange,
    getIsFilterOptionsAllSelected,
    isFilterOptionsAllSelected
  } = filters

  use([BarChart, GridComponent, SVGRenderer, TooltipComponent])

  const [isPlotNumOptionsOpen, setIsPlotNumOptionsOpen] = useState(false)
  const [plotNumQuestionId, setPlotNumQuestionId] = useState('')
  const [plotNumQuestion, setPlotNumQuestion] = useState<QuestionSchema | null>(null)
  const [isPlotCatOptionsOpen, setIsPlotCatOptionsOpen] = useState(false)
  const [plotCatQuestionId, setPlotCatQuestionId] = useState('')
  const [plotCatQuestion, setPlotCatQuestion] = useState<QuestionSchema | null>(null)
  const [infoQuestionId, setInfoQuestionId] = useState('')
  const [isGraphInfoContextMenuOpen, setIsGraphInfoContextMenuOpen] = useState(false)
  const [isContextMenuExpanded, setIsContextMenuExpanded] = useState<Record<string, boolean>>({})
  const [contextPerCategory, setContextPerCategory] = useState<Map<string, SubmissionSchema[]>>(
    new Map()
  )
  const [axisMap, setAxisMap] = useState(new Map())
  const chart = useRef<EChartsType>(null)
  const domElement = useRef(null)
  const graphRef = useRef(null)
  const plotNumQuestionsTriggerRef = useRef(null)
  const plotNumQuestionsContextRef = useRef(null)
  const plotCatQuestionsTriggerRef = useRef(null)
  const plotCatQuestionsContextRef = useRef(null)
  const graphInfoTriggerRef = useRef(null)
  const graphInfoContextRef = useRef(null)

  useOutsideClick(
    {
      element: plotNumQuestionsContextRef,
      trigger: plotNumQuestionsTriggerRef
    },
    () => setIsPlotNumOptionsOpen(false)
  )

  useOutsideClick(
    {
      element: plotCatQuestionsContextRef,
      trigger: plotCatQuestionsTriggerRef
    },
    () => setIsPlotCatOptionsOpen(false)
  )

  useOutsideClick({ element: graphInfoContextRef, trigger: graphInfoTriggerRef }, () =>
    setIsGraphInfoContextMenuOpen(false)
  )

  useEffect(() => {
    if (domElement.current) {
      chart.current = init(domElement.current, null, { renderer: 'svg' })
    }

    document.addEventListener('keyup', (e) => captureScreenshot(e, graphRef, []))

    return () => {
      document.removeEventListener('keyup', (e) => captureScreenshot(e, graphRef, []))
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    plotNumQuestions.length && setPlotNumQuestionId(plotNumQuestions[0].id)
  }, [plotNumQuestions])

  useEffect(() => {
    plotCatQuestions.length && setPlotCatQuestionId(plotCatQuestions[0].id)
  }, [plotCatQuestions])

  useEffect(() => {
    infoQuestions.length && handleInfoQuestionChange(getInfoQuestion())
    // eslint-disable-next-line
  }, [infoQuestions, plotCatQuestionId])

  useEffect(() => {
    const question = allQuestions.find(({ id }) => id === plotNumQuestionId)
    setPlotNumQuestion(question ?? null)

    // eslint-disable-next-line
  }, [plotNumQuestionId])

  useEffect(() => {
    const question = allQuestions.find(({ id }) => id === plotCatQuestionId)
    setPlotCatQuestion(question ?? null)

    // eslint-disable-next-line
  }, [plotCatQuestionId])

  useEffect(() => {
    let entriesPerCategory = new Map<string, number>()
    let contextPerCategory = new Map<string, SubmissionSchema[]>()

    if (plotCatQuestion) {
      const optionSet = filterOptions.get(plotCatQuestion.id)

      if (optionSet) {
        if (plotCatQuestion.type === 'dropdown') {
          plotCatQuestion.meta?.dropdownOptions?.values.forEach((option) => {
            if (optionSet.has(option)) {
              entriesPerCategory.set(option, 0)
            }
          })
        }

        if (plotCatQuestion.type === 'checkbox') {
          plotCatQuestion.meta?.checkboxOptions?.values.forEach((option) => {
            if (optionSet.has(option)) {
              entriesPerCategory.set(option, 0)
            }
          })
        }
      }

      submissions.forEach((submission, submissionIndex) => {
        let globalSectionIndex = -1
        let globalQuestionIndex = -1

        submissions[submissionIndex].sections.forEach(({ questions }, sIndex) => {
          questions.forEach(({ id }, qIndex) => {
            if (id === plotNumQuestionId) {
              globalSectionIndex = sIndex
              globalQuestionIndex = qIndex
            }
          })
        })

        const numAnswer =
          submissions[submissionIndex].sections[globalSectionIndex]?.questions[globalQuestionIndex]
            .answer

        submission.sections.forEach(({ questions }, sectionIndex) => {
          questions
            .filter(({ id }) => id === plotCatQuestionId)
            .filter(() => {
              let disqualify = false

              for (const question of plotCatQuestions) {
                const catQuestionIndex = submissions[submissionIndex].sections[
                  sectionIndex
                ].questions.findIndex(({ id }) => id === question.id)

                const catQuestion =
                  submissions[submissionIndex].sections[sectionIndex].questions[catQuestionIndex]

                if (catQuestion) {
                  const catAnswer = catQuestion.answer
                  const optionSet = filterOptions.get(question.id)

                  if (optionSet) {
                    if (catQuestion.type === 'dropdown') {
                      if (!optionSet.has(catAnswer as string)) {
                        disqualify = true
                        break
                      }
                    }

                    if (catQuestion.type === 'checkbox' && Array.isArray(catAnswer)) {
                      let isAnswerInSet = false

                      for (const answer of catAnswer) {
                        if (optionSet.has(answer)) {
                          isAnswerInSet = true
                          break
                        }
                      }

                      if (!isAnswerInSet) {
                        disqualify = true
                        break
                      }
                    }
                  }
                }
              }

              return !disqualify
            })
            .forEach(({ answer }) => {
              const optionSet = filterOptions.get(plotCatQuestion.id)

              if (optionSet) {
                if (plotNumQuestionId) {
                  if (plotCatQuestion.type === 'dropdown' && typeof answer === 'string') {
                    ;({ entriesPerCategory, contextPerCategory } = manageCatTotals({
                      entriesPerCategory,
                      contextPerCategory,
                      submission,
                      answer,
                      value: Number(numAnswer)
                    }))
                  } else if (plotCatQuestion.type === 'checkbox' && Array.isArray(answer)) {
                    answer.forEach((option) => {
                      if (optionSet.has(option)) {
                        ;({ entriesPerCategory, contextPerCategory } = manageCatTotals({
                          entriesPerCategory,
                          contextPerCategory,
                          submission,
                          answer: option,
                          value: Number(numAnswer)
                        }))
                      }
                    })
                  }
                } else {
                  if (plotCatQuestion.type === 'dropdown' && typeof answer === 'string') {
                    ;({ entriesPerCategory, contextPerCategory } = manageCatTotals({
                      entriesPerCategory,
                      contextPerCategory,
                      submission,
                      answer
                    }))
                  } else if (plotCatQuestion.type === 'checkbox' && Array.isArray(answer)) {
                    answer.forEach((option) => {
                      if (optionSet.has(option)) {
                        ;({ entriesPerCategory } = manageCatTotals({
                          entriesPerCategory,
                          contextPerCategory,
                          submission,
                          answer: option
                        }))
                      }
                    })
                  }
                }
              }
            })
        })
      })
    }

    setContextPerCategory(contextPerCategory)
    setAxisMap(isDemo ? barChartDemo : entriesPerCategory)

    // eslint-disable-next-line
  }, [submissions, plotNumQuestionId, plotCatQuestionId, plotCatQuestion, filterOptions])

  useEffect(() => {
    const options = {
      grid: { top: '0%', right: '0%', bottom: '7%', left: '0%' },
      xAxis: {
        type: 'category',
        name: 'Options',
        nameTextStyle: { fontFamily: 'Archivo', fontWeight: 'bold' },
        data: Array.from(axisMap.keys()),
        axisTick: { show: true, alignWithLabel: true }
      },
      yAxis: {
        type: 'value',
        name: `Units: ${plotNumQuestion?.meta?.numberOptions?.unit ? plotNumQuestion.meta.numberOptions.unit : 'Values'}`,
        nameTextStyle: { fontFamily: 'Archivo', fontWeight: 'bold' },
        boundaryGap: [0, 0.1],
        nameGap: 20,
        offset: 5,
        splitLine: {
          show: true,
          lineStyle: { type: 'dashed' }
        }
      },
      tooltip: {
        className: 'graph_tooltip',
        trigger: 'axis',
        enterable: true,
        formatter: (params: any) => {
          const { name, value } = params[0]

          let primaryValue = ''
          const unit = plotNumQuestion?.meta?.numberOptions?.unit

          if (value) {
            primaryValue = formatNum(value)
          }

          if (unit) {
            primaryValue = `${primaryValue} ${unit}`
          }

          const optionSubmissions = contextPerCategory.get(name)
          const allQuestionsContext: ContextSchema[] = []
          let questionContext: ContextSchema

          if (optionSubmissions) {
            optionSubmissions.forEach(({ sections }) => {
              sections.forEach(({ questions }) =>
                questions.forEach(({ id, answer }) => {
                  if (id === infoQuestionId) {
                    questionContext = {
                      ...questionContext,
                      context: answer ?? 'N/A'
                    }
                  }

                  if (id === plotNumQuestionId && typeof answer === 'number') {
                    questionContext = { ...questionContext, value: answer }
                  }
                })
              )

              allQuestionsContext.push(questionContext)
            })
          }

          const htmlContent = allQuestionsContext.map((question) => {
            if (question) {
              const contextStr = Array.isArray(question.context)
                ? question.context.join(', ')
                : typeof question.context === 'number'
                  ? parseDate(question.context, 'date')
                  : typeof question.context === 'boolean'
                    ? question.context
                      ? 'Yes'
                      : 'N/A'
                    : question.context?.toString() || 'N/A'

              let valueStr = question.value ? formatNum(question.value) : ''
              const unit = plotNumQuestion?.meta?.numberOptions?.unit

              if (valueStr && unit) {
                valueStr = `${valueStr} ${unit}`
              }

              return `
                <span ${!valueStr && `style="grid-column: 1/3"`}>${format.encodeHTML(contextStr)}</span>
                ${valueStr && `<span>${format.encodeHTML(valueStr)}</span>`}
              `
            } else {
              return `
                <span style="grid-column: 1/3">N/A</span>
              `
            }
          })

          return `
            <span class="graph_tooltip-header">${Number.isNaN(Number(name)) ? '' : 'Value: '}${format.encodeHTML(name)}</span>
            ${
              primaryValue &&
              `
                <br />
                <span style="color: ${format.encodeHTML(theme.hex)}">${format.encodeHTML(primaryValue)}</span>
                ${
                  infoQuestionId &&
                  `<div class="graph_tooltip-context">${htmlContent.join('')}</div>`
                }
              `
            }
          `
        }
      },
      series: [
        {
          type: 'bar',
          data: Array.from(axisMap.values()),
          barWidth: '60%',
          itemStyle: {
            color: theme.hex,
            borderRadius: [8, 8, 0, 0]
          }
        }
      ]
    }

    options && chart.current && chart.current.setOption(options)

    // eslint-disable-next-line
  }, [axisMap, infoQuestionId])

  const handlePlotNumQuestionChange = (questionId: string) => setPlotNumQuestionId(questionId)

  const handlePlotCatQuestionChange = (questionId: string) => setPlotCatQuestionId(questionId)

  const handleInfoQuestionChange = (questionId: string) => setInfoQuestionId(questionId)

  const handleExpandContextMenu = (ref: HTMLDivElement, wrapped: boolean = false) => {
    toggleExpandContextMenu(ref, isContextMenuExpanded, setIsContextMenuExpanded, wrapped)
  }

  const getInfoQuestion = () => {
    for (const { id } of infoQuestions) {
      if (id !== plotCatQuestionId) {
        return id
      }
    }

    return ''
  }

  return (
    <>
      {plotCatQuestions.length ? (
        <div className='view_tab_content' ref={graphRef}>
          <div className='actions_container'>
            <div className='actions-select actions-select-fg'>
              <span className='material-symbols-rounded'>bar_chart</span>
              <div className='actions_series'>
                <div>
                  <div
                    id='plot_num_questions_trigger'
                    className='div-with-icon'
                    onClick={() => setIsPlotNumOptionsOpen(!isPlotNumOptionsOpen)}
                    ref={plotNumQuestionsTriggerRef}>
                    <span id='plot_num_questions_trigger'>Y-axis</span>
                    <span id='plot_num_questions_trigger' className='material-symbols-rounded'>
                      {isPlotNumOptionsOpen ? 'arrow_drop_down' : 'arrow_right'}
                    </span>
                  </div>
                  {isPlotNumOptionsOpen && (
                    <div
                      className={`context_menu context_menu-left context_menu-down context_menu-${
                        isContextMenuExpanded['plotNumQuestionsContext'] ? 'lg' : 'md'
                      }`}
                      id='plotNumQuestionsContext'
                      ref={plotNumQuestionsContextRef}>
                      <div className='context_menu-grid context-header context-header-defaults'>
                        <div
                          onClick={() =>
                            plotNumQuestionsContextRef.current &&
                            handleExpandContextMenu(plotNumQuestionsContextRef.current)
                          }>
                          <h4>Values</h4>
                          <span className='material-symbols-rounded'>
                            {isContextMenuExpanded['plotNumQuestionsContext']
                              ? 'collapse_content'
                              : 'expand_content'}
                          </span>
                        </div>
                        <input
                          id='checkbox_by_entry'
                          type='checkbox'
                          checked={!plotNumQuestionId}
                          onChange={() => handlePlotNumQuestionChange('')}
                        />
                        <label htmlFor='checkbox_by_entry' className='highlight-styled-hover'>
                          {`${form?.isPublic ? 'Responses' : 'Entries'}`}
                        </label>
                      </div>
                      {form?.sections.map(({ id: sectionId, name, questions }) => {
                        const hasQuestions = questions.some(({ type }) => type === 'number')

                        let numericOptions = false

                        for (const question of questions) {
                          const isEveryOptionNumeric = question.meta?.dropdownOptions?.values.every(
                            (option) => !Number.isNaN(Number(option))
                          )

                          if (isEveryOptionNumeric) {
                            numericOptions = true
                            break
                          }
                        }

                        if (!hasQuestions && !numericOptions) {
                          return <Fragment key={sectionId} />
                        }

                        return (
                          <div className='context_menu-grouped' key={sectionId}>
                            <h5>{name}</h5>
                            <div className='context_menu-grid'>
                              {questions.map(({ id: questionId, type, statement }) => {
                                if (type === 'number' || (type === 'dropdown' && numericOptions)) {
                                  return (
                                    <Fragment key={questionId}>
                                      <input
                                        id={`checkbox_${questionId}`}
                                        type='checkbox'
                                        checked={plotNumQuestionId === questionId}
                                        onChange={() => handlePlotNumQuestionChange(questionId)}
                                      />
                                      <label
                                        htmlFor={`checkbox_${questionId}`}
                                        className='highlight-styled-hover'>
                                        {statement}
                                      </label>
                                    </Fragment>
                                  )
                                } else {
                                  return <Fragment key={questionId} />
                                }
                              })}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
              {isDemo ? (
                <span className='actions-active'>Q1 expenditure</span>
              ) : plotNumQuestion ? (
                <span
                  className='actions-active'
                  title={`${plotNumQuestion.statement}${
                    plotNumQuestion.description ? ' (' + plotNumQuestion.description + ')' : ''
                  }`}>
                  {plotNumQuestion.statement}
                  {plotNumQuestion.meta?.numberOptions?.unit &&
                    ` (${plotNumQuestion.meta.numberOptions.unit})`}
                </span>
              ) : (
                <span className='actions-active'>
                  {`${form?.isPublic ? 'Responses' : 'Entries'}`}
                </span>
              )}
            </div>
            <div className='actions-select actions-select-filter'>
              <div>
                <span className='material-symbols-rounded material-symbols-sm'>event_list</span>
                {!isFilterOptionsAllSelected && (
                  <span className='on-indicator' title='Some filters have been applied'>
                    ●
                  </span>
                )}
              </div>
              <div className='actions_series'>
                <div>
                  <div
                    id='plot_cat_questions_trigger2'
                    className='div-with-icon'
                    onClick={() => setIsFilterOptionsOpen(!isFilterOptionsOpen)}
                    ref={filterOptionsTriggerRef}>
                    <span id='plot_cat_questions_trigger2'>Show filters</span>
                    <span id='plot_cat_questions_trigger2' className='material-symbols-rounded'>
                      {isFilterOptionsOpen ? 'arrow_drop_down' : 'arrow_right'}
                    </span>
                  </div>
                  {isFilterOptionsOpen && (
                    <div
                      className={`context_menu context_menu-down context_menu-${
                        isContextMenuExpanded['filterOptionsContext'] ? 'lg' : 'md'
                      }`}
                      id='filterOptionsContext'
                      ref={filterOptionsContextRef}>
                      <div className='context_menu-grid context-header'>
                        <div
                          onClick={() =>
                            filterOptionsContextRef.current &&
                            handleExpandContextMenu(filterOptionsContextRef.current)
                          }>
                          <h4>Values</h4>
                          <span className='material-symbols-rounded'>
                            {isContextMenuExpanded['filterOptionsContext']
                              ? 'collapse_content'
                              : 'expand_content'}
                          </span>
                        </div>
                      </div>
                      {form?.sections.map(({ id: sectionId, name, questions }) => {
                        const hasQuestion = questions.find(({ id }) => id === plotCatQuestionId)

                        if (!hasQuestion) {
                          return <Fragment key={sectionId} />
                        }

                        return (
                          <div className='context_menu-grouped' key={sectionId}>
                            <h5>{name}</h5>
                            {questions.map(({ id, type, statement, meta }) => {
                              if (id !== plotCatQuestionId) {
                                return <Fragment key={id} />
                              }

                              return (
                                <div className='context_menu-grouped' key={id}>
                                  <span>{statement}</span>
                                  <div className='context_menu-grid'>
                                    <input
                                      id={`checkbox_${id}_all`}
                                      type='checkbox'
                                      checked={getIsFilterOptionsAllSelected(id)}
                                      onChange={() => handleFilterOptionsChange(id, '')}
                                    />
                                    <label
                                      htmlFor={`checkbox_${id}_all`}
                                      className='highlight-styled-hover'>
                                      All
                                    </label>
                                    {type === 'dropdown' &&
                                      meta?.dropdownOptions?.values.map((option, index) => (
                                        <Fragment key={index}>
                                          <input
                                            id={`checkbox_${id}_${option}`}
                                            type='checkbox'
                                            checked={filterOptions.get(id)?.has(option)}
                                            onChange={() => handleFilterOptionsChange(id, option)}
                                          />
                                          <label
                                            htmlFor={`checkbox_${id}_${option}`}
                                            className='highlight-styled-hover'>
                                            {option}
                                          </label>
                                        </Fragment>
                                      ))}
                                    {type === 'checkbox' &&
                                      meta?.checkboxOptions?.values.map((option, index) => (
                                        <Fragment key={index}>
                                          <input
                                            id={`checkbox_${id}_${option}`}
                                            type='checkbox'
                                            checked={filterOptions.get(id)?.has(option)}
                                            onChange={() => handleFilterOptionsChange(id, option)}
                                          />
                                          <label
                                            htmlFor={`checkbox_${id}_${option}`}
                                            className='highlight-styled-hover'>
                                            {option}
                                          </label>
                                        </Fragment>
                                      ))}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div ref={domElement} className='graph' />
          <div className='actions_container'>
            <div className='actions-select actions-select-fg'>
              <span className='material-symbols-rounded'>sort</span>
              <div className='actions_series'>
                <div>
                  <div
                    id='plot_cat_questions_trigger'
                    className='div-with-icon'
                    onClick={() => setIsPlotCatOptionsOpen(!isPlotCatOptionsOpen)}
                    ref={plotCatQuestionsTriggerRef}>
                    <span id='plot_cat_questions_trigger'>X-axis</span>
                    <span id='plot_cat_questions_trigger' className='material-symbols-rounded'>
                      {isPlotCatOptionsOpen ? 'arrow_drop_up' : 'arrow_right'}
                    </span>
                  </div>
                  {isPlotCatOptionsOpen && (
                    <div
                      className={`context_menu context_menu-left context_menu-up context_menu-${
                        isContextMenuExpanded['plotCatQuestionsContext'] ? 'lg' : 'md'
                      }`}
                      id='plotCatQuestionsContext'
                      ref={plotCatQuestionsContextRef}>
                      <div className='context_menu-grid context-header'>
                        <div
                          onClick={() =>
                            plotCatQuestionsContextRef.current &&
                            handleExpandContextMenu(plotCatQuestionsContextRef.current)
                          }>
                          <h4>Options</h4>
                          <span className='material-symbols-rounded'>
                            {isContextMenuExpanded['plotCatQuestionsContext']
                              ? 'collapse_content'
                              : 'expand_content'}
                          </span>
                        </div>
                      </div>
                      {form?.sections.map(({ id: sectionId, name, questions }) => {
                        const hasQuestions = questions.some(
                          ({ type }) => type === 'checkbox' || type === 'dropdown'
                        )

                        if (!hasQuestions) {
                          return <Fragment key={sectionId} />
                        }

                        return (
                          <div className='context_menu-grouped' key={sectionId}>
                            <h5>{name}</h5>
                            <div className='context_menu-grid'>
                              {questions.map(({ id: questionId, type, statement }) => {
                                if (type === 'checkbox' || type === 'dropdown') {
                                  return (
                                    <Fragment key={questionId}>
                                      <input
                                        id={`checkbox_${questionId}`}
                                        type='checkbox'
                                        checked={plotCatQuestionId === questionId}
                                        onChange={() => handlePlotCatQuestionChange(questionId)}
                                      />
                                      <label
                                        htmlFor={`checkbox_${questionId}`}
                                        className='highlight-styled-hover'>
                                        {statement}
                                      </label>
                                    </Fragment>
                                  )
                                } else {
                                  return <Fragment key={questionId} />
                                }
                              })}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
              {isDemo ? (
                <span className='actions-active'>Departments</span>
              ) : (
                plotCatQuestion && (
                  <span
                    className='actions-active'
                    title={`${plotCatQuestion.statement}${
                      plotCatQuestion.description ? ' (' + plotCatQuestion.description + ')' : ''
                    }`}>
                    {plotCatQuestion.statement}
                  </span>
                )
              )}
            </div>
            <div className='graph_options'>
              {!!infoQuestions.length && (
                <div>
                  <span
                    id='graph_context_trigger'
                    className='material-symbols-rounded material-symbols-sm'
                    onClick={() => setIsGraphInfoContextMenuOpen(!isGraphInfoContextMenuOpen)}
                    ref={graphInfoTriggerRef}>
                    info
                  </span>
                  {isGraphInfoContextMenuOpen && (
                    <div
                      className={`context_menu context_menu-up context_menu-${
                        isContextMenuExpanded['graphInfoContext'] ? 'lg' : 'md'
                      }`}
                      id='graphInfoContext'
                      ref={graphInfoContextRef}>
                      <div className='context_menu-grid context-header context-header-defaults'>
                        <div
                          onClick={() =>
                            graphInfoContextRef.current &&
                            handleExpandContextMenu(graphInfoContextRef.current)
                          }>
                          <h4>Values</h4>
                          <span className='material-symbols-rounded'>
                            {isContextMenuExpanded['graphInfoContext']
                              ? 'collapse_content'
                              : 'expand_content'}
                          </span>
                        </div>
                        <input
                          id='checkbox_no_info'
                          type='checkbox'
                          checked={!!infoQuestionId}
                          onChange={() =>
                            handleInfoQuestionChange(infoQuestionId ? '' : getInfoQuestion())
                          }
                        />
                        <label htmlFor='checkbox_no_info' className='highlight-styled-hover'>
                          Show info
                        </label>
                      </div>
                      {form?.sections.map(({ id: sectionId, name, questions }) => {
                        const hasQuestions = questions.some(
                          ({ id, type }) => type !== 'number' && id !== plotCatQuestionId
                        )

                        if (!hasQuestions) {
                          return <Fragment key={sectionId} />
                        }

                        return (
                          <div className='context_menu-grouped' key={sectionId}>
                            <h5>{name}</h5>
                            <div className='context_menu-grid'>
                              {questions.map(({ id: questionId, type, statement }) => {
                                if (type !== 'number' && questionId !== plotCatQuestionId) {
                                  return (
                                    <Fragment key={questionId}>
                                      <input
                                        id={`checkbox_${questionId}`}
                                        type='checkbox'
                                        checked={infoQuestionId === questionId}
                                        onChange={() => handleInfoQuestionChange(questionId)}
                                      />
                                      <label
                                        htmlFor={`checkbox_${questionId}`}
                                        className='highlight-styled-hover'>
                                        {statement}
                                      </label>
                                    </Fragment>
                                  )
                                } else {
                                  return <Fragment key={questionId} />
                                }
                              })}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <span className='no_results'>No values available for this graph.</span>
      )}
    </>
  )
}
